<template>
  <AppDialog :is-open="isDialogOpened"
             :ok-disabled="isConfirmUpgradeDisabled"
             :ok-loading="roomStorageAddonPending"
             :ok-text="$t('common.confirm')"
             size="l"
             @cancel="closeDialog"
             @ok="onConfirmUpgrade"
  >
    <template #title>{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.title') }}</template>
    <template #body>
      <template v-if="dataRoomCustomPricingPending">
        <v-skeleton-loader type="article"/>
      </template>
      <template v-else>
        <p class="text--small text--bold">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferTitle') }}</p>

        <div class="offer">
          <div class="offer__details">
            <div>
              <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferDataroomLabel"
                      tag="p"
                      class="text--medium"
                      scope="global"
              >
                <template #storage>
                  {{ getDisplaySize(currentRoom.dataroomInfo.initialStorageGb) }}
                </template>
                <template #duration>
                  <span v-t="{
                    path: 'project.project-settings.ProjectSettingsDataroomUpgradeDialog.monthsCount',
                    plural: currentRoom.dataroomInfo.initialDuration,
                    args: { months: currentRoom.dataroomInfo.initialDuration },
                  }" />
                </template>
              </i18n-t>

              <p class="text-grey mt-2 text--x-small">
                {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.subscriptionDateLabel', { date: ISOToDDMMYYYY(currentRoom.createdAt)}) }}
              </p>
            </div>
            <span class="ml-auto text--medium text--bold">{{ getDisplayPrice(currentRoom.dataroomInfo.monthlyPrice) }}</span>
            <span class="text-grey text--small">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}</span>
          </div>
          <div v-for="(addon, index) in currentRoom.dataroomInfo.addOns" :key="index" class="offer__details">
            <div>
              <p class="text--medium">
                {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferAddonLabel')}} - {{ getDisplaySize(addon.sizeGb) }}
              </p>
              <p class="text-grey text--x-small">
                {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.subscriptionDateLabel', { date: ISOToDDMMYYYY(addon.date)}) }}
              </p>
            </div>
            <span class="ml-auto text--medium text--bold">{{ getDisplayPrice(addon.monthlyPrice) }}</span>
            <span class="text-grey text--small">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}</span>
          </div>

          <!-- Total Monthly Price -->
          <div class="offer__details">
            <p class="text-grey text--small">
              {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.total') }}
            </p>
            <span class="text-grey ml-auto text--small">
              {{ getDisplayPrice(totalMonthlyPrice) }}
            </span>
            <span class="text-grey text--x-small">
              {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
            </span>
          </div>
        </div>

        <div class="d-flex justify-space-between align-center mb-2">
          <span class="text--small text--bold">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageTitle') }}</span>
          <v-chip v-if="dataRoomCustomPricing.customerDiscountPercent"
                  color="dataroom lighten-5"
                  class="font-weight-medium px-2 rounded-xl"
                  label
                  size="small"
          >
            {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.customerDiscount', { percent: dataRoomCustomPricing.customerDiscountPercent }) }}
          </v-chip>
        </div>

        <p class="text-grey mb-4 text--small">
          {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSubTitle') }}
        </p>

        <v-radio-group v-model="v$.selectedStorageQuantity.$model"
                       hide-details
                       class="addons"
        >
          <v-radio v-for="nb in addonsDisplayedNumber"
                   :key="nb"
                   :value="nb"
                   class="addon"
                   :class="{ 'addon--active': selectedStorageQuantity === nb }"
          >
            <template #label>
              <span class="addon__size text--medium">
                {{ getDisplaySize(dataRoomCustomPricing.additionalStorageSize * nb) }}
              </span>
              <div class="ml-auto">
                <template v-if="dataRoomCustomPricing.customerDiscountPercent">
                  <span class="text-grey text-decoration-line-through mr-2 text--small">
                    {{ getDisplayPrice(dataRoomCustomPricing.additionalStoragePrice * nb) }}
                    {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                  </span>
                </template>
                <span class="text--medium text--bold">
                  {{ getDisplayPrice(dataRoomCustomPricing.additionalStoragePrice * nb, dataRoomCustomPricing.customerDiscountPercent) }}
                </span>
                <span class="text-grey text--small">
                  {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                </span>
              </div>
            </template>
          </v-radio>
          <v-radio :value="selectedStorageWiderQuantity"
                   class="addon"
                   :class="{ 'addon--active': selectedStorageQuantity && selectedStorageQuantity === selectedStorageWiderQuantity }"
          >
            <template #label>
              <span class="addon__size pa-0 text--medium">
                <AppSelect v-model="selectedStorageWiderQuantity"
                           :items="computedStorageWiderItems"
                           :placeholder="$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStoragePlaceholder')"
                           class="addon__select"
                           noLabel
                           @update:model-value="onStorageWiderQuantityChange"
                />
              </span>
              <div>
                <template v-if="dataRoomCustomPricing.customerDiscountPercent && selectedStorageWiderQuantity">
                  <span class="text-grey text-decoration-line-through mr-2 text--small">
                    {{ getDisplayPrice(dataRoomCustomPricing.additionalStoragePrice * selectedStorageWiderQuantity) }}{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                  </span>
                </template>
                <span class="ml-auto text--medium text--bold">{{ computedStorageWiderDisplayPrice }}</span>
                <span class="text-grey text--small">
                  {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                </span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <div v-if="selectedStorageQuantity" class="mt-4">
          <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSumUpText"
                  tag="p"
                  class="text--medium"
                  scope="global"
          >
            <template #price>
              <span class="text--small text--bold">
                {{ totalAdditionalStorageDisplayPrice }}
              </span>
            </template>
          </i18n-t>
        </div>

        <v-checkbox v-model="v$.termsChecked.$model"
                    :error-messages="termsCheckedError"
                    class="terms__checkbox"
        >
          <template #label>
            <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsLabel"
                    tag="span"
                    scope="global"
            >
              <template #link>
                <AppDownloadFile :link="termsLink">
                  <template #default="{ submitForm }">
                    <span class="text-blue-darken-2 text-decoration-underline"
                          @click="submitForm"
                    >
                      {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsLabelLink') }}
                    </span>
                  </template>
                </AppDownloadFile>
              </template>
            </i18n-t>
          </template>
        </v-checkbox>
      </template>
    </template>
  </AppDialog>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { defineComponent } from 'vue'
import { mapActions,  mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { ISOToDDMMYYYY } from '@/common/utils/dates'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { POST_ROOM_STORAGE_ADDON } from '@/store/modules/room/action_types'
import { GET_DATA_ROOM_CUSTOM_PRICING } from '@/store/modules/rooms/action_types'

export default defineComponent({
  name: 'ProjectSettingsDataroomUpgradeDialog',
  components: { AppDialog, AppDownloadFile, AppSelect },
  setup: () => ({ v$: useVuelidate() }),
  mixins: [dialogMixin],
  data () {
    return {
      ISOToDDMMYYYY,
      addonsDisplayedNumber: 3,
      addonsMaxNumber: 9,
      selectedStorageQuantity: null,
      selectedStorageWiderQuantity: null,
      termsChecked: false,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom', 'roomStorageAddonPending']),
    ...mapState('rooms', ['dataRoomCustomPricing', 'dataRoomCustomPricingPending']),
    totalMonthlyPrice () {
      let total = parseFloat(this.currentRoom.dataroomInfo.monthlyPrice)

      this.currentRoom.dataroomInfo.addOns.forEach((addon) => {
        total += parseFloat(addon.monthlyPrice)
      })

      return total
    },
    computedStorageWiderItems () {
      const items = []

      for (let i = this.addonsDisplayedNumber; i < this.addonsMaxNumber; i++) {
        const item = {
          title: `${this.getDisplaySize(this.dataRoomCustomPricing.additionalStorageSize * (i + 1))}`,
          value: i + 1,
        }
        items.push(item)
      }

      return items
    },
    computedStorageWiderDisplayPrice () {
      if (this.selectedStorageWiderQuantity) {
        return this.getDisplayPrice(this.dataRoomCustomPricing.additionalStoragePrice * this.selectedStorageWiderQuantity, this.dataRoomCustomPricing.customerDiscountPercent)
      }

      return this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice', { price: '- ' })
    },
    isConfirmUpgradeDisabled () {
      return this.v$.$invalid
    },
    termsCheckedError () {
      return formFieldErrors(
        this.v$.termsChecked,
        [
          ['sameAs', this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsValidationError')],
        ],
      )
    },
    termsLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=dataroom-price-list-${this.$i18n.locale.toUpperCase()}`
    },
    totalAdditionalStorageSize () {
      return (this.selectedStorageQuantity * this.dataRoomCustomPricing.additionalStorageSize).toFixed(1)
    },
    totalAdditionalStorageDisplayPrice () {
      return this.getDisplayPrice(this.selectedStorageQuantity * this.dataRoomCustomPricing.additionalStoragePrice, this.dataRoomCustomPricing.customerDiscountPercent)
    },
  },
  created () {
    try {
      this.GET_DATA_ROOM_CUSTOM_PRICING({
        duration: this.currentRoom.dataroomInfo.initialDuration,
        storage: this.currentRoom.dataroomInfo.initialStorageGb,
        unit: 'Gb',
      })
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    ...mapActions('room', [POST_ROOM_STORAGE_ADDON]),
    ...mapActions('rooms', [GET_DATA_ROOM_CUSTOM_PRICING]),
    getDiscountPrice (price, discountPercent) {
      const discount = (price * discountPercent) / 100
      return price - discount
    },
    getDisplayPrice (price, discountPercent) {
      price = parseFloat(price)
      const finalPrice = discountPercent ? this.getDiscountPrice(price, discountPercent) : price
      return this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice', { price: this.$n(finalPrice, 'decimal') })
    },
    getDisplaySize (size) {
      const formattedSize = parseFloat(Number(size).toFixed(1))

      if (formattedSize < 1) {
        return `${formattedSize * 1000} ${this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.sizeUnit.Mb')}`
      } else {
        return `${formattedSize} ${this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.sizeUnit.Gb')}`
      }
    },
    async onConfirmUpgrade () {
       try {
          await this.POST_ROOM_STORAGE_ADDON({
            mnemo: this.currentRoom.mnemo,
            data: {
              storage: this.totalAdditionalStorageSize,
              unit: 'Gb',
            },
          })

          this.$emit('refresh-stats')
          toast.success(this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSuccessText'))
          this.closeDialog()
        } catch (e) {
          toast.error(this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageErrorText'))
        }
    },
    onStorageWiderQuantityChange () {
      this.selectedStorageQuantity = this.selectedStorageWiderQuantity
    },
  },
  validations: {
    selectedStorageQuantity: { required },
    termsChecked: { sameAs: sameAs(true) },
  },
})
</script>

<style scoped lang="scss">
.offer {
  background-color: rgb(var(--v-theme-grey-50));
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;
  margin-top: 16px;

  &__details {
    align-items: center;
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 1px solid rgb(var(--v-theme-grey-300));
      margin-bottom: 16px;
      padding-bottom: 8px;
    }
  }
}

.addons {
  display: block;

  :deep(.v-input__control) {
    display: block;

    .v-selection-control-group {
      row-gap: 8px;
    }

    .v-field__outline__start,
    .v-field__outline__end {
      border: none;
    }
  }
}

.addon {
  display: block;
  margin: 0 !important;
  border-radius: 8px;
  cursor: pointer;
  outline: 1px solid rgb(var(--v-theme-grey-300));
  outline-offset: -1px;

  &--active {
    outline-color: #4C75B5;
    outline-width: 2px;
  }

  &__size {
    background-color: rgb(var(--v-theme-grey-50));
    border-radius: 4px;
    padding: 8px;
    min-width: 110px;
    text-align: center;
  }

  &__select {
    min-width: 200px;
  }

  :deep(.v-selection-control__wrapper) {
    display: none;
  }

  :deep(.v-label) {
    width: 100%;
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
  }
}

.terms__checkbox {
   margin-top: 24px;
   padding-top: 0;

  :deep(.v-input__slot) {
    align-items: start;
  }
}
</style>
